import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, PhoneIncoming, FileCheck, Building2, MessageSquare, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DashboardShell, StatCard } from "@/components/dashboard/DashboardShell";
import { LocationAutocomplete } from "@/components/LocationAutocomplete";
import { api } from "@/lib/api";

const NAV = [
  { key: "overview", label: "Dashboard", icon: LayoutDashboard },
  { key: "leads", label: "Taxi Requests", icon: PhoneIncoming },
  { key: "applications", label: "Onboarding Leads", icon: FileCheck },
  { key: "corporate", label: "Corporate Leads", icon: Building2 },
  { key: "chats", label: "Conversations", icon: MessageSquare },
];

export default function AgentDashboard() {
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [d, setD] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const load = () => api.get("/agent/dashboard").then(({ data }) => setD(data));
  useEffect(() => {
    load();
    api.post("/support/presence", { online: true }).catch(() => {});
    return () => { api.post("/support/presence", { online: false }).catch(() => {}); };
  }, []);

  return (
    <DashboardShell title="Agent" nav={NAV} active={active} onNav={(k) => k === "chats" ? navigate("/messages") : setActive(k)}>
      {!d ? <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div> : (
        <div className="space-y-6">
          <div className="flex justify-end">
            <Button className="gap-2 bg-brand hover:bg-brand/90" onClick={() => setBookOpen(true)} data-testid="agent-create-booking"><Plus size={16} /> Create Booking for Customer</Button>
          </div>
          {active === "overview" && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard icon={PhoneIncoming} label="Taxi requests" value={d.leads.length} color="amber" />
              <StatCard icon={FileCheck} label="Onboarding leads" value={d.driver_applications.length} />
              <StatCard icon={Building2} label="Corporate leads" value={d.corporate.length} color="navy" />
              <StatCard icon={MessageSquare} label="Conversations" value={d.conversations.length} color="emerald" />
            </div>
          )}
          {active === "leads" && <List items={d.leads} render={(l) => (
            <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="font-semibold text-navy">{l.id} · {l.name}</p>
              <p className="text-sm text-slate-500">{l.pickup?.name} → {l.dropoff?.name || "—"} · {l.phone}</p>
              <p className="text-xs text-slate-400">Status: {l.status}</p>
            </div>
          )} empty="No taxi requests." />}
          {active === "applications" && <List items={d.driver_applications} render={(a) => (
            <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4"><p className="font-semibold text-navy">{a.application_id} · {a.personal?.name}</p><p className="text-sm text-slate-500">{a.business?.operator_name} · {a.status}</p></div>
          )} empty="No onboarding leads." />}
          {active === "corporate" && <List items={d.corporate} render={(c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4"><p className="font-semibold text-navy">{c.company}</p><p className="text-sm text-slate-500">{c.contact} · {c.email}</p></div>
          )} empty="No corporate leads." />}
        </div>
      )}
      <AgentBookingDialog open={bookOpen} onOpenChange={setBookOpen} />
    </DashboardShell>
  );
}

function List({ items, render, empty }) {
  if (!items?.length) return <p className="text-sm text-slate-500">{empty}</p>;
  return <div className="grid gap-3">{items.map(render)}</div>;
}

function AgentBookingDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: new Date().toISOString().split("T")[0], time: "10:00", passengers: 1 });
  const [loading, setLoading] = useState(false);

  const find = async () => {
    if (!pickup || !dropoff) { toast.error("Select pickup and destination"); return; }
    setLoading(true);
    try { const { data } = await api.post("/search", { pickup, dropoff, service_type: "local", vehicle_key: "any", passengers: form.passengers, time: form.time }); setOffers(data.offers.slice(0, 5)); }
    finally { setLoading(false); }
  };
  const book = async (o) => {
    const { data } = await api.post("/bookings", {
      service_type: "local", pickup, dropoff, date: form.date, time: form.time,
      passengers: { adults: form.passengers, children: 0, infants: 0 }, luggage: { small: 0, large: 0 },
      vehicle_id: o.vehicle_id, vehicle_key: o.vehicle_key,
      customer: { name: form.name, email: form.email, phone: form.phone }, created_by: "agent",
    });
    toast.success(`Booking ${data.booking_ref} created for ${form.name}`);
    onOpenChange(false); navigate(`/confirmation/${data.booking_ref}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Create Booking for Customer</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-cust-name" />
            <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="agent-cust-phone" />
            <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="agent-cust-email" />
          </div>
          <LocationAutocomplete value={pickup} onSelect={setPickup} placeholder="Pickup" testId="agent-pickup" />
          <LocationAutocomplete value={dropoff} onSelect={setDropoff} placeholder="Destination" testId="agent-dropoff" />
          <div className="grid grid-cols-3 gap-2">
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
            <Input type="number" min={1} value={form.passengers} onChange={(e) => setForm({ ...form, passengers: parseInt(e.target.value) || 1 })} />
          </div>
          <Button onClick={find} disabled={loading} className="bg-navy hover:bg-navy/90" data-testid="agent-find-taxis">{loading ? "Searching…" : "Find Available Taxis"}</Button>
          {offers.map((o) => (
            <div key={o.offer_id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div><p className="text-sm font-medium text-navy">{o.title}</p><p className="text-xs text-slate-500">{o.operator_name} · £{o.total.toFixed(2)}</p></div>
              <Button size="sm" className="bg-brand hover:bg-brand/90" onClick={() => book(o)} data-testid={`agent-book-${o.offer_id}`}>Book</Button>
            </div>
          ))}
        </div>
        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
}
